/**
 * useValue Hook
 *
 * 核心职责:
 * 1. 管理 value 状态 (支持受控/非受控模式)
 * 2. 提供 handleChange 方法处理选择变化
 * 3. 提供 clearSelection 方法清空选择
 * 4. 管理 isClear 标志位
 *
 * 设计要点:
 * - 受控模式: 使用 props.value,状态更新由外部控制
 * - 非受控模式: 使用内部 state,自主管理状态
 * - 避免闭包陷阱: useCallback 依赖数组明确声明
 */

import { useState, useEffect, useCallback } from 'react';
import type { CascaderOption } from '../iCascader';

export interface UseValueProps {
    value?: string[];
    defaultValue?: string[];
    multiple?: boolean;
    onChange?: (value: string[], selectedOptions?: CascaderOption[]) => void;
}

export interface UseValueReturn {
    value: string[];
    isClear: boolean;
    handleChange: (value: string[], selectedOptions?: CascaderOption[], originSelectOptions?: CascaderOption[]) => void;
    clearValue: () => void;
    setValue: (value: string[], selectedOptions?: CascaderOption[], originSelectOptions?: CascaderOption[]) => void;
}

/**
 * useValue Hook
 *
 * @param props - UseValueProps
 * @returns UseValueReturn
 */
export const useValue = (props: UseValueProps): UseValueReturn => {
    const { value: propsValue, defaultValue, multiple, onChange } = props;

    // 内部状态管理
    // 修复: 确保defaultValue在非受控模式下被正确设置
    const [internalValue, setInternalValue] = useState<string[]>(() => {
        if ('value' in props && propsValue) {
            return propsValue;
        }
        return defaultValue || [];
    });
    const [isClear, setIsClear] = useState(false);

    // 是否为受控模式
    const isControlled = 'value' in props && propsValue !== undefined;

    // 当前实际使用的 value
    const value = isControlled ? (propsValue || []) : internalValue;

    // 受控模式: 监听外部 props.value 变化
    useEffect(() => {
        if (isControlled && propsValue instanceof Array) {
            setInternalValue(propsValue);
        }
    }, [isControlled, propsValue]);

    /**
     * setValue - 统一的 value 设置方法
     *
     * @param newValue - 新的 value 值
     * @param selectedOptions - 选中的选项 (单选模式)
     * @param originSelectOptions - 原始选中选项 (多选模式)
     */
    const setValue = useCallback(
        (
            newValue: string[],
            selectedOptions?: CascaderOption[],
            originSelectOptions?: CascaderOption[]
        ) => {
            // 非受控模式: 更新内部状态
            if (!isControlled) {
                setInternalValue(newValue);
            }

            // 触发 onChange 回调
            if (onChange) {
                if (multiple) {
                    onChange(newValue, originSelectOptions);
                } else {
                    onChange(newValue, selectedOptions);
                }
            }
        },
        [isControlled, multiple, onChange]
    );

    /**
     * handleChange - 处理选择变化
     *
     * @param newValue - 新的 value 值
     * @param selectedOptions - 选中的选项 (单选模式)
     * @param originSelectOptions - 原始选中选项 (多选模式)
     */
    const handleChange = useCallback(
        (
            newValue: string[],
            selectedOptions?: CascaderOption[],
            originSelectOptions?: CascaderOption[]
        ) => {
            // 重置 isClear 标志
            setIsClear(false);

            // 调用 setValue 更新状态
            setValue(newValue, selectedOptions, originSelectOptions);
        },
        [setValue]
    );

    /**
     * clearValue - 清空选择
     */
    const clearValue = useCallback(() => {
        // 设置 isClear 标志
        setIsClear(true);

        // 清空 value,传递空数组作为selectedOptions
        setValue([], []);
    }, [setValue]);

    return {
        value,
        isClear,
        handleChange,
        clearValue,
        setValue,
    };
};
